import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Bell, Copy, Home, Keyboard, Map, Moon, Plus, Search, Settings, StickyNote, Sun, Tag, Users, UserPlus, Monitor } from "lucide-react";
import { Avatar, Kbd, Pill } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import { useCommandBarStore, useLayoutStore, useOpenContact, type CommandItem } from "@/app/navigation";
import { useTheme } from "@/app/theme";
import { NetworkCommandItems } from "@/features/network";
import { NoteEditor } from "@/features/contact/NoteEditor";
import { ReminderEditor } from "@/features/contact/ReminderEditor";
import { rankWithScores, STRONG_MATCH } from "./match";
import s from "./commandbar.module.css";

interface Row {
  kind: "command" | "person";
  id: string;
  title: string;
  subtitle?: string;
  shortcut?: string;
  group: string;
  icon?: React.ReactNode;
  run: () => void;
}

export function CommandBar() {
  const open = useCommandBarStore((st) => st.open);
  const initialQuery = useCommandBarStore((st) => st.initialQuery);
  const setOpen = useCommandBarStore((st) => st.setOpen);
  const version = useCommandBarStore((st) => st.version);
  const registry = useCommandBarStore((st) => st.items);
  const register = useCommandBarStore((st) => st.register);
  const navigate = useNavigate();
  const openContact = useOpenContact();
  const theme = useTheme();
  const setShortcutsOpen = useLayoutStore((st) => st.setShortcutsOpen);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const [modal, setModal] = useState<"note" | "reminder" | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const networkItems = NetworkCommandItems();

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, initialQuery]);

  const people = useEngineQuery("queryContacts", { query, limit: 6, sort: "score", direction: "desc" }, { enabled: open && query.trim().length > 1, placeholderData: (p) => p });

  const close = () => setOpen(false);
  const go = (to: string) => () => { navigate(to); close(); };

  const builtins = useMemo<CommandItem[]>(() => [
    { id: "nav.home", title: "Go to Home", keywords: ["home", "feed"], shortcut: "g h", group: "Navigate", icon: Home, run: go("/") },
    { id: "nav.people", title: "Go to People", keywords: ["people", "contacts", "list"], shortcut: "g p", group: "Navigate", icon: Users, run: go("/people") },
    { id: "nav.starred", title: "Go to Starred", keywords: ["starred", "favorites"], shortcut: "g s", group: "Navigate", icon: Users, run: go("/people?starred=1") },
    { id: "nav.notes", title: "Go to Notes", keywords: ["notes"], group: "Navigate", icon: StickyNote, run: go("/notes") },
    { id: "nav.reminders", title: "Go to Reminders", keywords: ["reminders"], group: "Navigate", icon: Bell, run: go("/reminders") },
    { id: "nav.search", title: "Search", keywords: ["search", "find"], shortcut: "/", group: "Navigate", icon: Search, run: go("/search") },
    { id: "nav.map", title: "Go to Map", keywords: ["map", "location", "cities"], group: "Navigate", icon: Map, run: go("/people/map") },
    { id: "nav.duplicates", title: "Review duplicates", keywords: ["duplicates", "merge"], group: "Navigate", icon: Copy, run: go("/people/duplicates") },
    { id: "nav.archive", title: "Go to Archive", keywords: ["archive", "archived"], group: "Navigate", icon: Archive, run: go("/archive") },
    { id: "nav.settings", title: "Open Settings", keywords: ["settings", "preferences"], group: "Navigate", icon: Settings, run: go("/settings") },
    { id: "create.person", title: "Create a person", keywords: ["create", "new", "person", "contact"], shortcut: "p", group: "Create", icon: UserPlus, run: go("/people/new") },
    { id: "create.note", title: "Create a note", keywords: ["create", "new", "note"], shortcut: "Mod+N", group: "Create", icon: StickyNote, run: () => { close(); setModal("note"); } },
    { id: "create.reminder", title: "Create a reminder", keywords: ["create", "new", "reminder"], group: "Create", icon: Bell, run: () => { close(); setModal("reminder"); } },
    { id: "create.group", title: "Create a group", keywords: ["create", "new", "group"], group: "Create", icon: Tag, run: go("/group/new") },
    { id: "theme.light", title: "Switch to light theme", keywords: ["theme", "light", "appearance"], group: "Appearance", icon: Sun, run: () => { theme.setTheme("light"); close(); } },
    { id: "theme.dark", title: "Switch to dark theme", keywords: ["theme", "dark", "appearance"], group: "Appearance", icon: Moon, run: () => { theme.setTheme("dark"); close(); } },
    { id: "theme.auto", title: "Match system theme", keywords: ["theme", "auto", "system"], group: "Appearance", icon: Monitor, run: () => { theme.setTheme("auto"); close(); } },
    { id: "help.shortcuts", title: "Keyboard shortcuts", keywords: ["shortcuts", "keyboard", "help"], shortcut: "?", group: "Help", icon: Keyboard, run: () => { close(); setShortcutsOpen(true); } },
    ...networkItems.map((n) => ({ id: n.id, title: n.title, keywords: n.keywords, shortcut: n.shortcut, group: "Network", run: () => { n.run(); close(); } })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, networkItems.length]);

  // Built-ins live in the registry too so their single-key shortcuts work from anywhere (see app/shortcuts.ts).
  useEffect(() => register(builtins), [builtins, register]);

  const rows = useMemo<Row[]>(() => {
    const all: CommandItem[] = registry().filter((i) => !i.hidden).map((i) => ({ ...i, run: () => { i.run(); close(); } }));
    const ranked = rankWithScores(query, all, query ? 30 : 60);
    const commandsFirst = (ranked[0]?.score ?? 0) >= STRONG_MATCH;
    const commandRows: Row[] = ranked.map(({ item: c }) => ({ kind: "command", id: c.id, title: c.title, shortcut: c.shortcut, group: c.group ?? "Actions", icon: c.icon ? <c.icon size={15} /> : <Plus size={15} />, run: c.run }));
    const peopleRows: Row[] = query.trim().length > 1 ? (people.data?.items ?? []).map((p) => ({ kind: "person", id: `p.${p.contactId}`, title: p.displayName, subtitle: p.headline ?? p.primaryEmail ?? undefined, group: "People", icon: <Avatar name={p.displayName} src={p.avatarUrl} size={22} tier={p.bucket} seed={p.contactId} />, run: () => { openContact(p.contactId, { page: true }); close(); } })) : [];
    return commandsFirst ? [...commandRows, ...peopleRows] : [...peopleRows, ...commandRows];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, people.data, builtins, version]);

  useEffect(() => setIndex(0), [query]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${index}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [index]);

  if (!open) {
    return (
      <>
        <NoteEditor open={modal === "note"} onClose={() => setModal(null)} />
        <ReminderEditor open={modal === "reminder"} onClose={() => setModal(null)} />
      </>
    );
  }

  let lastGroup = "";
  return (
    <>
      <div className={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && close()}>
        <div className={s.palette} role="dialog" aria-modal="true" aria-label="Command bar" data-testid="command-bar">
          <div className={s.inputRow}>
            <Search size={16} className="u-muted" />
            <input
              ref={inputRef}
              className={s.input}
              value={query}
              placeholder="Type a command or a name"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => Math.min(rows.length - 1, i + 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
                else if (e.key === "Enter") { e.preventDefault(); rows[index]?.run(); }
                else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
              }}
              aria-label="Command"
              aria-activedescendant={rows[index] ? `cmd-${rows[index].id}` : undefined}
            />
            <Kbd>Esc</Kbd>
          </div>
          <div className={s.list} ref={listRef} role="listbox">
            {rows.length === 0 && <div className={s.empty}>Nothing matches. Try a person's name or a verb like add, go, switch.</div>}
            {rows.map((r, i) => {
              const header = r.group !== lastGroup ? r.group : null;
              lastGroup = r.group;
              return (
                <div key={r.id}>
                  {header && <div className={s.groupHeader}>{header}</div>}
                  <div id={`cmd-${r.id}`} role="option" aria-selected={i === index} data-index={i} className={s.item} data-active={i === index ? "true" : undefined} onMouseEnter={() => setIndex(i)} onClick={() => r.run()}>
                    <span className={s.itemIcon}>{r.icon}</span>
                    <span className="u-grow" style={{ minWidth: 0 }}>
                      <span className="u-truncate" style={{ display: "block" }}>{r.title}</span>
                      {r.subtitle && <span className="u-small u-muted u-truncate" style={{ display: "block" }}>{r.subtitle}</span>}
                    </span>
                    {r.kind === "person" && <Pill>Open</Pill>}
                    {r.shortcut && <span className="u-row" style={{ gap: 3 }}>{r.shortcut.split(/[ +]/).map((k, j) => <Kbd key={j}>{k === "Mod" ? (navigator.platform.includes("Mac") ? "Cmd" : "Ctrl") : k}</Kbd>)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={s.footer}>
            <span><Kbd>Up</Kbd> <Kbd>Down</Kbd> move</span>
            <span><Kbd>Enter</Kbd> run</span>
            <span className="u-grow" />
            <span className="u-muted">{rows.length} results</span>
          </div>
        </div>
      </div>
      <NoteEditor open={modal === "note"} onClose={() => setModal(null)} />
      <ReminderEditor open={modal === "reminder"} onClose={() => setModal(null)} />
    </>
  );
}
